/**
 * Barra de filtros fixa (coleção e busca, mobile e desktop)
 *
 * A faixa de chips de tamanho (section própria) e a barra Filtros / Ordenar
 * (dentro da section principal) grudam logo abaixo do header, como um bloco só.
 * No modo sidebar, no desktop, a barra fica reduzida a uma linha de 1px
 * (component-facets.css), mas segue no fluxo: é a posição dela que marca o fim
 * da coleção, e a margem dela que compensa a compactação dos chips. Quem gruda é o CSS: position: sticky
 * em component-facets.css e em section-size-filter-chips.css. No desktop a
 * sidebar de filtros gruda logo abaixo do bloco (section-collection.css).
 * Este script cuida do que o CSS não sabe:
 *
 *  - a compactação, que acompanha a rolagem: ao grudar, o bloco encolhe na
 *    mesma medida em que a página rola (--sticky-filters-progress, de 0 a 1) e
 *    volta a crescer ao rolar de volta. Não há animação com tempo: o tamanho
 *    é função da posição, então segue o dedo, inclusive parando no meio;
 *  - as medidas que o CSS interpola (altura da faixa, quanto ela e o bloco
 *    encolhem). A altura que o bloco perde volta como margem abaixo da barra:
 *    a altura total no fluxo não muda, então a lista não se mexe e o ponto em
 *    que o bloco gruda fica parado;
 *  - o fim da lista: a faixa de chips é sticky no <main> inteiro e a barra só
 *    dentro da coleção, então a faixa é empurrada para fora junto com a barra.
 */
(() => {
  const facets = document.querySelector('facet-filters-form.collection-facets');
  if (!facets) return;

  const rootStyle = document.documentElement.style;
  const chips = document.querySelector('.section-size-filter-chips');

  // O progresso muda a cada quadro de rolagem. Ele é escrito só nos dois
  // elementos que o usam: no :root, cada mudança recalcularia o estilo da
  // página inteira.
  const progressTargets = chips ? [chips, facets] : [facets];

  // Onde o topo do bloco fica no fluxo. Um elemento sticky grudado não diz
  // mais onde estaria; este marcador vazio, logo antes dele, diz.
  const marker = document.createElement('div');
  marker.setAttribute('aria-hidden', 'true');
  (chips || facets).before(marker);

  let progress = 0;
  let range = 1;
  let pushUp = 0;
  let lastWidth = window.innerWidth;
  let updateFrame = null;
  let refreshFrame = null;

  const setPx = (name, value) => rootStyle.setProperty(name, `${value}px`);
  const height = element => (element ? element.getBoundingClientRect().height : 0);
  const facetsTop = () => parseFloat(getComputedStyle(facets).top) || 0;

  const setProgress = value => {
    const next = Math.round(value * 10000) / 10000;
    if (next === progress) return;
    progress = next;
    progressTargets.forEach(element => element.style.setProperty('--sticky-filters-progress', next));
  };

  // Distância do título (ou descrição) da faixa até os chips: é o quanto os
  // chips sobem por cima deles ao compactar. Zero quando não há título.
  const chipsHeadHeight = () => {
    const first = chips && chips.querySelector('.size-chips__heading, .size-chips__description');
    const track = chips && chips.querySelector('.size-chips__track');
    if (!first || !track) return 0;
    return track.getBoundingClientRect().top - first.getBoundingClientRect().top;
  };

  // Mede o bloco solto e compacto, tudo no mesmo frame (nada é pintado no meio).
  const measure = () => {
    const current = progress;

    setProgress(0);
    setPx('--sticky-filters-chips-head', chipsHeadHeight());
    const chipsExpanded = height(chips);
    const expanded = chipsExpanded + height(facets);

    setProgress(1);
    const chipsCompact = height(chips);
    const compact = chipsCompact + height(facets);

    setProgress(current);

    setPx('--sticky-filters-chips-height', chipsExpanded);
    setPx('--sticky-filters-chips-shrink', chipsExpanded - chipsCompact);
    setPx('--sticky-filters-shrink', expanded - compact);
    // Altura do bloco compacto, para âncoras e scrollIntoView pararem abaixo dele.
    setPx('--sticky-filters-height', compact);

    // O bloco encolhe na mesma velocidade em que a página rola: a borda de
    // baixo dele acompanha o conteúdo até ficar compacto.
    range = Math.max(1, expanded - compact);
  };

  const update = () => {
    updateFrame = null;

    // O `top` da barra é o que já fica fixo (anúncio + header) mais a altura
    // atual da faixa; tirando a faixa, sobra a linha em que o bloco gruda.
    const stickAt = facetsTop() - height(chips);
    const scrolledPast = window.scrollY > 0 ? stickAt - marker.getBoundingClientRect().top : 0;
    setProgress(Math.min(1, Math.max(0, scrolledPast / range)));

    if (!chips) return;

    // Fim da coleção: a barra começa a subir, e a faixa sobe o mesmo tanto.
    const next = Math.min(0, Math.round(facets.getBoundingClientRect().top - facetsTop()));
    if (next !== pushUp) {
      pushUp = next;
      chips.style.setProperty('--sticky-filters-push-up', `${pushUp}px`);
    }
  };

  const refresh = () => {
    refreshFrame = null;
    measure();
    update();
  };

  const requestUpdate = () => {
    if (updateFrame === null) updateFrame = requestAnimationFrame(update);
  };

  const requestRefresh = () => {
    if (refreshFrame === null) refreshFrame = requestAnimationFrame(refresh);
  };

  refresh();

  window.addEventListener('scroll', requestUpdate, { passive: true });

  // No celular a barra de endereço aparece e some ao rolar, disparando resize
  // só de altura. As alturas medidas dependem apenas da largura (é ela que
  // troca o layout entre mobile e desktop).
  window.addEventListener('resize', () => {
    if (window.innerWidth === lastWidth) {
      requestUpdate();
      return;
    }
    lastWidth = window.innerWidth;
    requestRefresh();
  });

  // Fontes carregadas mudam as alturas medidas no início.
  window.addEventListener('load', refresh);

  // A faixa some sozinha quando a página não tem filtro de tamanho
  // (size-filter-chips.js), depois desta primeira medição. Observa o atributo,
  // não o tamanho: o tamanho muda a cada quadro de rolagem.
  const chipsElement = chips && chips.querySelector('size-filter-chips');
  if (chipsElement) {
    new MutationObserver(requestRefresh).observe(chipsElement, {
      attributes: true,
      attributeFilter: ['hidden']
    });
  }

  // Filtrar troca o grid sem rolar a página: o fim da coleção pode mudar de
  // lugar e, com ele, o quanto a faixa precisa subir.
  if ('ResizeObserver' in window) {
    new ResizeObserver(requestUpdate).observe(facets.parentElement);
  }
})();
